import React, {
  type ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import * as Toast from '@radix-ui/react-toast';
import { ToastRoot, ToastViewport, ToastClose, ToastProgress } from './styles';
// import { truncateHtml } from '@/shared/libs/truncate-html';
import { Button } from '@/shared/ui/form/button/button';
import { Box } from '@/shared/ui/box/Box';
import { Image } from '@/shared/ui/image/Image';
import { uniqueId } from 'lodash-es';
import { useI18n } from '@locales/client';
import { useRouter } from 'next-nprogress-bar';

import {
  // BodyTextMediumC,
  // BodyTextRegularC,
  BodyTextRegularB,
  BodyTextMediumB,
} from '@/shared/ui/typography2';
import { StatusIcon, Status } from '@/features/notify-management/styles';
import { openLiveChat } from '@/shared/libs/live-chat';
import { createDialogCloseOnlyHandler } from '@/shared/libs/dialog-open-change';
import { useLocaleFormat } from '@/shared/hooks/useLocaleFormat';

export interface ToastNotify {
  type?: string;
  status?: string;
  title: string;
  text: string;
  link?: string;
  action?: string;
  level?: number;
  key?: string;
  save?: boolean;
  image?: string;
  typeForImg?: unknown;
  errorString?: unknown;
  args?: Record<string, unknown>;
}

interface ToastItemData extends ToastNotify {
  ts: string;
  key: string;
}

interface ToastContextValue {
  showToast: (notify: ToastNotify) => void;
  hideToast: (ts: string) => void;
  showErrorToast: (error: unknown, args?: Record<string, unknown>) => void;
}

interface ToastItemProps {
  toast: ToastItemData;
  hideToast: (ts: string) => void;
}

interface ToastProviderProps {
  children: ReactNode;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_ICONS_PATH = '/images/icons/toast';
const TOAST_ID_PREFIX = 'toast-';
const getStatusLevelIcon = (level: number) =>
  `${TOAST_ICONS_PATH}/status_level_${level}.svg`;

const TOAST_ICONS = {
  system: `${TOAST_ICONS_PATH}/system.svg`,
  bonus: `${TOAST_ICONS_PATH}/bonus.svg`,
  error: `${TOAST_ICONS_PATH}/warning.svg`,
  loyalty: getStatusLevelIcon,
  newLevel: getStatusLevelIcon,
};

const isToastIconKey = (
  type: string | undefined
): type is keyof typeof TOAST_ICONS =>
  type !== undefined && type in TOAST_ICONS;

interface ToastErrorFields {
  type?: unknown;
  error?: unknown;
  status?: unknown;
}

const toToastErrorFields = (error: unknown): ToastErrorFields =>
  typeof error === 'object' && error !== null ? error : {};

const isUntranslated = (value: string) => value.startsWith('common');

const appendUniqueToast = <T extends { key: string }>(toasts: T[], toast: T) =>
  toasts.some((item) => item.key === toast.key) ? toasts : [...toasts, toast];

const ToastContent = ({ toast, hideToast }: ToastItemProps) => {
  const router = useRouter();
  const formatUrl = useLocaleFormat();

  const handleToastClick = useCallback(() => {
    if (!toast.link) return;

    hideToast(toast.ts);

    if (toast.link.includes('openSupport=true')) {
      void openLiveChat();
      return;
    }

    router.push(formatUrl(toast.link));
  }, [toast, router, hideToast, formatUrl]);

  const isNewLevel = toast.title === 'newLevel';

  const renderIcon = useMemo(() => {
    let iconSrc;

    if (isNewLevel) {
      const level = parseInt(toast.text ?? '', 10) - 1;
      iconSrc = TOAST_ICONS.newLevel(level);
    } else if (toast.type === 'loyalty') {
      iconSrc = TOAST_ICONS.loyalty(toast.level || 0);
    } else {
      const icon = isToastIconKey(toast.type)
        ? TOAST_ICONS[toast.type]
        : undefined;
      iconSrc = (typeof icon === 'string' && icon) || TOAST_ICONS.error;
    }

    return (
      <Status $status={toast?.status}>
        <StatusIcon
          $status={toast?.status}
          src={iconSrc}
          width={24}
          height={24}
          alt=""
        />
      </Status>
    );
  }, [toast, isNewLevel]);

  return (
    <Box
      $display="flex"
      $gap={8}
      onClick={toast.link ? handleToastClick : undefined}
      role={toast.link ? 'button' : undefined}
    >
      {renderIcon}
      <Box $flex="1">
        <BodyTextMediumB color="#FFF">{toast?.title}</BodyTextMediumB>
        <BodyTextRegularB>{toast?.text}</BodyTextRegularB>

        {!isNewLevel && toast.action && (
          <Box $marginTop={6}>
            <Button
              variant="gray"
              onClick={() => toast.link && router.push(formatUrl(toast.link))}
              type="button"
              style={{ background: '#545454' }}
            >
              <BodyTextRegularB>{toast.action}</BodyTextRegularB>
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const TOAST_DURATION = 8000;

const ToastItem = ({ toast, hideToast }: ToastItemProps) => {
  const [radixPaused, setRadixPaused] = useState(false);
  const [tabHidden, setTabHidden] = useState(
    () => typeof document !== 'undefined' && document.hidden
  );
  const [progress, setProgress] = useState(1);

  const elapsedRef = useRef(0);
  const resumedAtRef = useRef(Date.now());
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const handleVisibilityChange = () => setTabHidden(document.hidden);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const paused = radixPaused || tabHidden;

  useEffect(() => {
    if (paused) {
      elapsedRef.current += Date.now() - resumedAtRef.current;
      return undefined;
    }

    resumedAtRef.current = Date.now();

    const tick = () => {
      const elapsed = elapsedRef.current + (Date.now() - resumedAtRef.current);
      setProgress(Math.max(0, 1 - elapsed / TOAST_DURATION));
      if (elapsed < TOAST_DURATION) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [paused]);

  return (
    <ToastRoot
      duration={TOAST_DURATION}
      $hasAction={!!toast.action}
      $status={toast.status}
      $type={toast.type}
      onOpenChange={createDialogCloseOnlyHandler(() => hideToast(toast.ts))}
      onPause={() => setRadixPaused(true)}
      onResume={() => setRadixPaused(false)}
    >
      <ToastContent toast={toast} hideToast={hideToast} />
      <ToastClose aria-label="close">
        <Image
          src="/images/icons/close-dialog-secondary.svg"
          width={16}
          height={16}
          alt="close"
        />
      </ToastClose>
      <ToastProgress
        $status={toast.status}
        style={{ transform: `scaleX(${progress})` }}
      />
    </ToastRoot>
  );
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastItemData[]>([]);
  const t = useI18n();

  const showErrorToast = useCallback(
    (error: unknown, args: Record<string, unknown> = {}) => {
      const errorFields = toToastErrorFields(error);
      const typeForImg = errorFields.type || 'error';
      const errorCode = errorFields.error || 'unknown';

      let title = t(`common.api_errors.${errorCode}.title`);
      let text = t(`common.api_errors.${errorCode}.desc`);

      const existsApiErrorKey = !(
        isUntranslated(title) || isUntranslated(text)
      );

      let existsKey = existsApiErrorKey;

      if (!existsApiErrorKey) {
        const statusKey = errorFields.status
          ? String(errorFields.status)
          : null;
        const statusMessage = statusKey && t(`common.errors.${statusKey}`);

        if (statusMessage && !isUntranslated(statusMessage)) {
          title = '';
          text = statusMessage;
          existsKey = true;
        }
      }

      if (existsKey) {
        const notify: ToastItemData = {
          ts: uniqueId(TOAST_ID_PREFIX),
          type: 'error',
          status: 'error',
          title,
          text,
          typeForImg,
          key: `error-${errorCode}`,
        };

        setToasts((prev) => appendUniqueToast(prev, notify));
      }
    },
    [t]
  );

  const showToast = useCallback(
    (notify: ToastNotify) => {
      if (notify.type === 'error' && notify.errorString) {
        return showErrorToast(notify.errorString, notify.args);
      }

      const newToast: ToastItemData = {
        ts: uniqueId(TOAST_ID_PREFIX),
        ...notify,
        key: notify.key || `${notify.title}-${notify.text}`,
      };

      setToasts((prev) => appendUniqueToast(prev, newToast));

      if (Notification.permission === 'granted' && notify.save) {
        // const notification =
        const notificationOptions: NotificationOptions & { image?: string } = {
          body: notify.text,
          image: notify?.image,
          // icon: notify?.image,
        };
        new Notification(notify.title, notificationOptions);

        // notification.onclick = (event) => {
        //   event.preventDefault();
        //   window.focus();
        // }
      }
    },
    [showErrorToast]
  );

  const hideToast = useCallback((ts: string) => {
    setToasts((prev) => prev.filter((toast) => toast.ts !== ts));
  }, []);

  const contextValue = useMemo<ToastContextValue>(
    () => ({
      showToast,
      hideToast,
      showErrorToast,
    }),
    [showToast, hideToast, showErrorToast]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      <Toast.Provider swipeDirection="up">
        {toasts.map((toast) => (
          <ToastItem key={toast.ts} toast={toast} hideToast={hideToast} />
        ))}
        <ToastViewport />
      </Toast.Provider>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
